import { Card, CardBody, RadioGroup, ScrollShadow, Spinner, Tab, Tabs } from "@nextui-org/react";
import { useCallback, useEffect, useRef, useState } from "react";
import RootAdmin from "../../../../components/layouts/RootAdmin";
import FilterReactSelect from "../../../../components/elements/FilterReactSelect";
import CardRadioButton from "../../../../components/fragments/admin/report/CardRadioButton.js";
import { filteredMaterialDataReportPurchase, hasValidRole } from "../../../../services/helpers";
import { getErrorMessage } from "../../../../services/errorMessage";
import ClientDataGrid from "../../../../components/elements/ClientDataGrid";
import useTablePurchase from "../../../../hooks/report/purchase/useTablePurchase";
import ModalAddMaterialPurchase from "../../../../components/fragments/admin/report/purchase/ModalAddMaterialPurchase";
import TableHistoryPurchase from "../../../../components/fragments/admin/report/purchase/TableHistoryPurchase";
import useAuth from "../../../../hooks/useAuth";
import RolesAccess from "../../../../enums/roles";
import TableReportPreview from "../../../../components/fragments/admin/report/preview/TableReportPreview";
import { patchFetcherWithoutId, deleteWithFormFetcherTest, postFetcher, putFetcherWithoutId } from "../../../../services/api";
import toast from "react-hot-toast";

// =====================================================================
// ENDPOINT
//
// Keduanya bekerja di level BAHAN, bukan per baris. Satu bahan punya satu
// baris pelaporan per bulan, jadi operasi per baris dari sisi frontend
// berisiko gagal separuh jalan dan meninggalkan data setengah terhapus.
// Lihat backend-additions.ts untuk implementasi kedua endpoint ini.
// =====================================================================
const updateNoRegistrasiApi = (payload) => patchFetcherWithoutId("/api/pelaporan-pembelian/material/no-surat-registrasi", payload);

const deleteMaterialApi = (payload) => deleteWithFormFetcherTest("/api/pelaporan-pembelian/material", payload);

// Membuat baris pelaporan untuk SELURUH SK impor perusahaan pada satu periode.
// Idempoten di sisi backend - kombinasi SK + bahan B3 yang sudah ada dilewati.
const generateSemuaSKApi = (payload) => postFetcher("/api/pelaporan-pembelian/generate-semua-sk", payload);

// Menandai satu bahan B3 tidak ada pembeliannya sepanjang periode: seluruh
// bulannya diisi 0 dan detail pembeliannya dihapus, dalam satu transaksi.
const setTanpaPembelianApi = (payload) => putFetcherWithoutId("/api/pelaporan-pembelian/material/tanpa-pembelian", payload);

export default function IndexPage() {
  const { roles } = useAuth();
  const {
    dataFetch: { data, isLoading, mutate, dataCompany, isLoadingCompany, dataPeriods, isLoadingPeriods },
    state: { selectedCompany, setSelectedCompany, selectedMaterial, setSelectedMaterial, selectedPeriod, setSelectedPeriod },
    columnsTableActivePeriod,
  } = useTablePurchase();

  const isAdminOrPengelola = hasValidRole(roles, [RolesAccess.PENGELOLA, RolesAccess.SUPER_ADMIN]);

  const [savingNoPurchaseId, setSavingNoPurchaseId] = useState(null);

  const materialData = filteredMaterialDataReportPurchase(data, isLoading);

  /*
   * Begitu perusahaan dan periode terpilih, seluruh SK impor perusahaan itu
   * langsung dibuatkan baris pelaporannya - menggantikan penambahan bahan B3
   * satu per satu lewat modal.
   *
   * Dijaga dengan ref agar satu kombinasi perusahaan+periode hanya ditembak
   * sekali per sesi halaman; tanpa itu, setiap render ulang akan memanggil
   * endpoint yang menulis data. Backend sendiri sudah idempoten, jadi ini
   * lapisan kedua, bukan satu-satunya pengaman.
   *
   * Diam bila tidak ada yang dibuat: perusahaan tanpa SK impor, atau yang SK-nya
   * sudah lengkap, adalah keadaan wajar - bukan kesalahan yang perlu
   * diberitahukan setiap kali halaman dibuka.
   */
  const sudahGenerate = useRef(new Set());

  useEffect(() => {
    const companyId = selectedCompany?.value;
    const periodId = selectedPeriod?.value;
    if (!companyId || !periodId) return;

    const kunci = `${companyId}|${periodId}`;
    if (sudahGenerate.current.has(kunci)) return;
    sudahGenerate.current.add(kunci);

    (async () => {
      try {
        const hasil = await generateSemuaSKApi({ companyId, periodId });
        if (hasil?.barisDibuat > 0) {
          toast.success(hasil.message);
          await mutate();
        }
        if (hasil?.casTidakDikenal?.length > 0) {
          toast.error(`Bahan B3 tidak ditemukan di master: ${hasil.casTidakDikenal.join(", ")}`);
        }
      } catch (error) {
        // Gagal di sini tidak boleh mengunci halaman - daftar yang sudah ada
        // tetap bisa dibuka. Kunci dilepas supaya percobaan berikutnya jalan.
        sudahGenerate.current.delete(kunci);
        toast.error(getErrorMessage(error, "Gagal membuat laporan dari daftar SK"));
      }
    })();
  }, [selectedCompany?.value, selectedPeriod?.value, mutate]);

  /**
   * Ambil baris pelaporan milik satu bahan.
   *
   * `materialId` bisa berupa registrasiId ATAU dataBahanB3Id, tergantung apakah
   * bahan tersebut memiliki registrasi. Logika ini sebelumnya ditulis inline di
   * filteredData; sekarang dipakai bersama oleh tabel, update, dan hapus supaya
   * ketiganya tidak mungkin memberi hasil yang berbeda.
   */
  const getRowsForMaterial = useCallback(
    (materialId) => {
      const rows = data?.data ?? [];
      if (!materialId) return [];
      const byRegistrasi = rows.filter((item) => item.registrasiId === materialId);
      return byRegistrasi.length > 0 ? byRegistrasi : rows.filter((item) => item.dataBahanB3Id === materialId);
    },
    [data?.data],
  );

  const filteredData = selectedMaterial && !isLoading && data ? getRowsForMaterial(selectedMaterial) : [];

  /**
   * Ambil identitas bahan dari baris pertamanya.
   *
   * `material.id` bisa berupa registrasi.id ATAU dataBahanB3Id (lihat
   * filteredMaterialDataReportPurchase di services/helpers). Baris pelaporan
   * adalah satu-satunya tempat kedua id itu tersedia sekaligus.
   */
  const getMaterialIdentity = useCallback(
    (materialId) => {
      const [firstRow] = getRowsForMaterial(materialId);
      if (!firstRow) return null;
      return {
        dataBahanB3Id: firstRow.dataBahanB3Id,
        registrasiId: firstRow.registrasiId ?? null,
      };
    },
    [getRowsForMaterial],
  );

  // Simpan no. registrasi baru untuk seluruh bulan bahan tsb pada periode aktif.
  const handleUpdateNoRegistrasi = useCallback(
    async (material, noRegistrasiBaru) => {
      const identity = getMaterialIdentity(material?.id);
      if (!identity) throw new Error("Tidak ada baris pelaporan untuk bahan ini");

      // Bahan berbasis registrasi mengambil nomornya dari record Registrasi.
      if (identity.registrasiId) {
        throw new Error("No. registrasi bahan ini berasal dari data registrasi B3 dan tidak dapat diubah di sini");
      }

      await updateNoRegistrasiApi({
        companyId: selectedCompany?.value,
        periodId: selectedPeriod?.value,
        dataBahanB3Id: identity.dataBahanB3Id,
        no_surat_registrasi: noRegistrasiBaru,
      });

      await mutate();
    },
    [getMaterialIdentity, mutate, selectedCompany?.value, selectedPeriod?.value],
  );

  /**
   * Penanda "Tidak Ada Pembelian B3" disimpan sebagai kombinasi
   * jumlahPembelianB3 = 0 dan laporanTersedia = false pada SELURUH bulan bahan
   * tersebut - persis kombinasi yang dibaca kolom Status di tabel sebelah kanan
   * untuk menampilkan label "Disimpan" (lihat useTablePurchase).
   *
   * Kartu mewakili satu periode, sedangkan satu bahan punya satu baris per
   * bulan, jadi cakupannya seluruh bulan - sama dengan aksi hapus di kartu ini.
   */
  const isNoPurchase = useCallback(
    (materialId) => {
      const rows = getRowsForMaterial(materialId);
      return rows.length > 0 && rows.every((row) => row.laporanTersedia === false);
    },
    [getRowsForMaterial],
  );

  const handleToggleNoPurchase = useCallback(
    async (material, checked) => {
      const identity = getMaterialIdentity(material?.id);
      if (!identity) {
        toast.error("Tidak ada baris pelaporan untuk bahan ini");
        return;
      }

      setSavingNoPurchaseId(material?.id);
      try {
        // Satu panggilan transaksional, bukan loop per bulan: mencentang berarti
        // menghapus detail seluruh bulan, dan kegagalan separuh jalan akan
        // menyisakan sebagian bulan bernilai 0 dan sebagian tidak.
        const respons = await setTanpaPembelianApi({
          companyId: selectedCompany?.value,
          periodId: selectedPeriod?.value,
          ...(identity.registrasiId ? { registrasiId: identity.registrasiId } : { dataBahanB3Id: identity.dataBahanB3Id }),
          tanpaPembelian: checked,
        });

        toast.success(respons?.message || (checked ? "Ditandai tidak ada pembelian B3" : "Penanda tidak ada pembelian B3 dilepas"));
        await mutate();
      } catch (error) {
        toast.error(getErrorMessage(error, "Gagal menyimpan penanda tidak ada pembelian B3"));
      } finally {
        setSavingNoPurchaseId(null);
      }
    },
    [getMaterialIdentity, mutate, selectedCompany?.value, selectedPeriod?.value],
  );

  // Hapus seluruh baris pelaporan bahan tsb, lalu bersihkan pilihan aktif.
  const handleDeleteMaterial = useCallback(
    async (material) => {
      const identity = getMaterialIdentity(material?.id);
      if (!identity) throw new Error("Tidak ada baris pelaporan untuk bahan ini");

      await deleteMaterialApi({
        companyId: selectedCompany?.value,
        periodId: selectedPeriod?.value,
        ...(identity.registrasiId ? { registrasiId: identity.registrasiId } : { dataBahanB3Id: identity.dataBahanB3Id }),
      });

      // Kosongkan pilihan bila bahan yang dihapus sedang dipilih, agar tabel di
      // sebelah kanan tidak menampilkan data yang sudah tidak ada.
      if (selectedMaterial === material?.id) setSelectedMaterial(null);

      await mutate();
    },
    [getMaterialIdentity, mutate, selectedCompany?.value, selectedPeriod?.value, selectedMaterial, setSelectedMaterial],
  );

  return (
    <RootAdmin>
      <Card radius="sm">
        <CardBody>
          <Tabs
            color="primary"
            variant="underlined"
            aria-label="tabs"
            classNames={{
              tabList: "gap-6 w-full relative rounded-none p-0 border-b border-divider",
              cursor: "w-full",
              tab: "max-w-fit px-1.5 h-12",
              tabContent: "group-data-[selected=true]:font-semibold",
            }}
          >
            {isAdminOrPengelola && (
              <Tab key="1" title="Periode Aktif">
                <div className="h-[550px] mt-3">
                  <div className="flex justify-between gap-2 mb-5">
                    <div className="flex gap-2">
                      <div className="flex flex-col justify-end">
                        <ModalAddMaterialPurchase mutate={mutate} setSelectedCompany={setSelectedCompany} setSelectedMaterial={setSelectedMaterial} setSelectedPeriod={setSelectedPeriod} />
                      </div>
                    </div>
                    <div className="w-full flex justify-end items-center gap-2">
                      <div className="w-1/4">
                        <FilterReactSelect
                          defaultValue={selectedPeriod}
                          placeholder="Cari Periode"
                          options={dataPeriods?.data?.map((item) => ({ label: item.name, value: item.id }))}
                          isLoading={isLoadingPeriods}
                          setValue={setSelectedPeriod}
                        />
                      </div>
                      <div className="w-1/4">
                        <FilterReactSelect
                          defaultValue={selectedCompany}
                          placeholder="Cari Perusahaan"
                          options={dataCompany?.data?.map((item) => ({ label: item.name, value: item.id }))}
                          isLoading={isLoadingCompany}
                          setValue={setSelectedCompany}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-3">
                    <div className="col-span-1 h-[420px]">
                      {!data ? (
                        <div className="h-full flex flex-row items-center justify-center border rounded-md">
                          <h3>Tidak ada data</h3>
                        </div>
                      ) : isLoading ? (
                        <div className="h-full flex flex-row items-center justify-center border rounded-md">
                          <Spinner className="mx-auto" />
                        </div>
                      ) : data?.data?.length === 0 ? (
                        <div className="h-full flex flex-row items-center justify-center border rounded-md">
                          <h3>Tidak ada data</h3>
                        </div>
                      ) : (
                        <ScrollShadow hideScrollBar className="h-full">
                          <RadioGroup value={selectedMaterial} onValueChange={setSelectedMaterial}>
                            {materialData?.map((item) => (
                              <CardRadioButton
                                key={item?.id}
                                value={item?.id}
                                description={item}
                                canManage={isAdminOrPengelola}
                                canEditNoRegistrasi={!getMaterialIdentity(item?.id)?.registrasiId}
                                infoTambahan={[
                                  { label: "Tipe Pembelian", value: item?.tipePembelian },
                                  // Pemasok hanya ada pada pembelian lokal -
                                  // kosong untuk impor, dan baris kosong tidak
                                  // ikut dicetak.
                                  { label: "Pemasok", value: item?.pemasok },
                                ]}
                                onUpdateNoRegistrasi={handleUpdateNoRegistrasi}
                                onDelete={handleDeleteMaterial}
                                noDataLabel="Tidak Ada Pembelian B3"
                                noData={isNoPurchase(item?.id)}
                                isSavingNoData={savingNoPurchaseId === item?.id}
                                onToggleNoData={handleToggleNoPurchase}
                              />
                            ))}
                          </RadioGroup>
                        </ScrollShadow>
                      )}
                    </div>
                    <div className="col-span-3 h-[420px]">
                      <ClientDataGrid
                        columns={columnsTableActivePeriod}
                        rows={selectedMaterial ? filteredData : []}
                        isLoading={isLoading}
                        initialState={{
                          sorting: {
                            sortModel: [{ field: "id", sort: "asc" }],
                          },
                        }}
                      />
                    </div>
                  </div>
                </div>
              </Tab>
            )}
            {isAdminOrPengelola && (
              <Tab key="2" title="Preview">
                <TableReportPreview
                  isPreviewOnly
                  selectedCompany={selectedCompany}
                  setSelectedCompany={setSelectedCompany}
                  selectedPeriod={selectedPeriod}
                  setSelectedPeriod={setSelectedPeriod}
                />
              </Tab>
            )}
            <Tab key="3" title="Riwayat">
              <TableHistoryPurchase
                dataCompany={dataCompany}
                isLoadingCompany={isLoadingCompany}
                selectedCompany={selectedCompany}
                setSelectedCompany={setSelectedCompany}
                selectedPeriod={selectedPeriod}
                setSelectedPeriod={setSelectedPeriod}
              />
            </Tab>
          </Tabs>
        </CardBody>
      </Card>
    </RootAdmin>
  );
}
